import { useRef, useState } from "react";
import { toast } from "sonner";
import { Upload, CheckCircle2, XCircle } from "lucide-react";
import { api, errMsg, eur } from "@/lib/api";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

export const ImportXmlDialog = ({ onDone }) => {
  const ref = useRef(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [results, setResults] = useState([]);

  const upload = async (files) => {
    if (!files?.length) return;
    const fd = new FormData();
    Array.from(files).forEach((f) => fd.append("files", f));
    setBusy(true);
    try {
      const { data } = await api.post("/invoices/import", fd);
      setResults(data.results);
      const ok = data.results.filter((r) => r.ok).length;
      toast.success(`${ok} documenti importati su ${data.results.length}`);
      onDone?.();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
      if (ref.current) ref.current.value = "";
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setResults([]); }}>
      <DialogTrigger asChild>
        <Button variant="outline" data-testid="import-xml-button" className="bg-white"><Upload className="mr-2 h-4 w-4" />Importa XML</Button>
      </DialogTrigger>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>Importa fatture XML</DialogTitle>
          <DialogDescription>File .xml o .xml.p7m in formato FatturaPA. Le fatture dove sei cedente vengono archiviate come emesse, le altre come ricevute.</DialogDescription>
        </DialogHeader>
        <label
          data-testid="import-dropzone"
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => { e.preventDefault(); upload(e.dataTransfer.files); }}
          className="flex cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed border-slate-300 bg-slate-50 px-6 py-10 text-center transition-colors hover:border-sky-400 hover:bg-sky-50"
        >
          <Upload className="mb-2 h-6 w-6 text-slate-400" />
          <span className="text-sm font-medium text-slate-700">{busy ? "Importazione in corso…" : "Trascina qui i file o clicca per selezionarli"}</span>
          <input ref={ref} data-testid="import-file-input" type="file" multiple accept=".xml,.p7m" className="hidden" onChange={(e) => upload(e.target.files)} />
        </label>
        {results.length > 0 && (
          <ul data-testid="import-results" className="max-h-60 space-y-1.5 overflow-auto text-sm">
            {results.map((r, i) => (
              <li key={i} className="flex items-start gap-2">
                {r.ok ? <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" /> : <XCircle className="mt-0.5 h-4 w-4 shrink-0 text-rose-600" />}
                <span className="min-w-0">
                  <span className="font-mono text-xs text-slate-500">{r.file}</span><br />
                  {r.ok ? `${r.direction === "emessa" ? "Emessa" : "Ricevuta"} n. ${r.numero} · ${r.controparte} · ${eur(r.totale)}` : <span className="text-rose-700">{r.error}</span>}
                </span>
              </li>
            ))}
          </ul>
        )}
      </DialogContent>
    </Dialog>
  );
};
